import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Plus,
  Search,
  MoreHorizontal,
  Trash2,
  Pencil,
  IdCard as IdCardIcon,
  ArrowDownAZ,
  Mail,
  Phone,
  AlertTriangle,
} from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import {
  EmployeeFormSheet,
  STATUS_OPTIONS,
  STATUS_DOT_TONES,
  EmployeeStatusBadge,
  fullName,
  initials,
  formatDateHu,
} from "@/components/EmployeeFormSheet";

// Sync with backend REQUIRED_FIELDS in /app/backend/routers/contract_templates.py.
// `titulus` is intentionally NOT in this list — the user explicitly stated
// it is optional.
const REQUIRED_FIELDS = [
  "last_name",
  "first_name",
  "mother_name",
  "birth_place",
  "birth_date",
  "citizenship",
  "taj",
  "tax_id",
  "id_card_number",
  "pos_id",
  "pos_password",
  "address",
  "email",
  "phone",
  "company_id",
  "site_id",
  "position",
  "employment_type",
  "employment_start_date",
];

function isIncomplete(emp) {
  if (!emp) return false;
  for (const key of REQUIRED_FIELDS) {
    const v = emp[key];
    if (v === null || v === undefined || (typeof v === "string" && !v.trim())) {
      return true;
    }
  }
  return false;
}

export default function EmployeesPage() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  const [open, setOpen] = useState(false);  const fetchAll = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/employees");
      setItems(data);
    } catch (e) {
      toast.error(
        formatApiError(e?.response?.data?.detail) ||
          "Nem sikerült betölteni a munkavállalókat."
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return items
      .filter((emp) => {
        if (statusFilter === "all") return true;
        if (statusFilter === "onboarding")
          return emp.onboarding_complete === false;
        if (statusFilter === "incomplete") return isIncomplete(emp);
        return emp.status === statusFilter;
      })
      .filter((emp) => {
        if (!q) return true;
        const hay = [
          emp.last_name,
          emp.first_name,
          emp.email,
          emp.taj,
          emp.tax_id,
          emp.phone,
          emp.birth_place,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        return hay.includes(q);
      })
      // Az onboarding-folyamatban lévő dolgozók kerüljenek a lista tetejére.
      .sort((a, b) => {
        const ao = a.onboarding_complete === false ? 0 : 1;
        const bo = b.onboarding_complete === false ? 0 : 1;
        return ao - bo;
      });
  }, [items, query, statusFilter]);

  const stats = useMemo(() => {
    const total = items.length;
    const counts = STATUS_OPTIONS.reduce(
      (acc, s) => ({ ...acc, [s.value]: 0 }),
      {}
    );
    let incomplete = 0;
    let onboarding = 0;
    items.forEach((e) => {
      if (e.status in counts) counts[e.status] += 1;
      if (isIncomplete(e)) incomplete += 1;
      if (e.onboarding_complete === false) onboarding += 1;
    });
    return { total, incomplete, onboarding, ...counts };
  }, [items]);

  const onDelete = async (emp) => {
    if (
      !window.confirm(
        `Biztosan törlöd "${fullName(emp)}" munkavállalót? Ez nem visszavonható.`
      )
    )
      return;
    try {
      await api.delete(`/employees/${emp._id}`);
      toast.success("Munkavállaló törölve.");
      fetchAll();
    } catch (err) {
      toast.error(
        formatApiError(err?.response?.data?.detail) || "Nem sikerült törölni."
      );
    }
  };

  return (
    <div data-testid="employees-page" className="space-y-8">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <p className="text-[11px] font-medium uppercase tracking-[0.18em] text-muted-foreground">
            Csapat
          </p>
          <h1 className="mt-1 text-2xl font-semibold tracking-tight sm:text-3xl">
            Munkavállalók
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Munkavállalói nyilvántartás — kattints egy sorra a részletes adatlap megtekintéséhez.
          </p>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-6">
        <StatCard label="Összes" value={stats.total} testId="stat-total" />
        <StatCard
          label="Onboarding"
          value={stats.onboarding}
          testId="stat-onboarding"
          tone="indigo"
          active={statusFilter === "onboarding"}
          onClick={() =>
            setStatusFilter((prev) => (prev === "onboarding" ? "all" : "onboarding"))
          }
        />
        <StatCard label="Aktív" value={stats.active} testId="stat-active" tone="emerald" />
        <StatCard label="Próbaidő" value={stats.probation} testId="stat-probation" tone="amber" />
        <StatCard label="Felmondás" value={stats.notice} testId="stat-notice" tone="orange" />
        <StatCard label="Inaktív" value={stats.inactive} testId="stat-inactive" />
      </div>

      {/* Toolbar + Table */}
      <div className="overflow-hidden rounded-xl border border-border bg-[hsl(var(--sipx-elevated))]">
        <div className="flex flex-wrap items-center gap-3 border-b border-border px-4 py-3">
          <div className="flex flex-1 items-center gap-2">
            <Search
              className="h-4 w-4 text-muted-foreground"
              strokeWidth={1.75}
              aria-hidden
            />
            <input
              data-testid="employees-search-input"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Keresés név, e-mail, TAJ vagy adószám alapján…"
              className="h-8 w-full bg-transparent text-sm outline-none placeholder:text-muted-foreground"
            />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger
              data-testid="employees-status-filter"
              className="h-8 w-[170px]"
            >
              <SelectValue placeholder="Állapot" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Minden állapot</SelectItem>
              <SelectItem value="onboarding">
                <span className="inline-flex items-center gap-1.5">
                  <span className="h-1.5 w-1.5 rounded-full bg-indigo-500" />
                  Onboarding
                </span>
              </SelectItem>
              <SelectItem value="incomplete">
                <span className="inline-flex items-center gap-1.5">
                  <AlertTriangle className="h-3 w-3 text-rose-500" />
                  Hiányos adatok
                </span>
              </SelectItem>
              {STATUS_OPTIONS.map((s) => (
                <SelectItem key={s.value} value={s.value}>
                  {s.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Mobile card list */}
        <div className="space-y-2 px-3 py-3 md:hidden" data-testid="employees-mobile-list">
          {loading ? (
            <p className="px-1 py-8 text-center text-sm text-muted-foreground">Betöltés…</p>
          ) : filtered.length === 0 ? (
            <EmptyState
              onAction={() => setOpen(true)}
              hasQuery={query.length > 0 || statusFilter !== "all"}
            />
          ) : (
            filtered.map((emp) => {
              const incomplete = isIncomplete(emp);
              const inOnboarding = emp.onboarding_complete === false;
              return (
                <button
                  key={emp._id}
                  type="button"
                  data-testid={`employee-card-${emp._id}`}
                  onClick={() => navigate(`/employees/${emp._id}`)}
                  className={cn(
                    "flex w-full items-center gap-3 rounded-2xl border border-border bg-card px-3 py-2.5 text-left transition-colors active:bg-foreground/[0.04]",
                    inOnboarding && "border-l-2 border-l-indigo-400 bg-indigo-50/30 dark:bg-indigo-500/[0.04]"
                  )}
                >
                  <span className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-foreground/90 text-background text-[11px] font-semibold">
                    {initials(emp)}
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-[13.5px] font-medium">
                      {emp.titulus ? `${emp.titulus} ` : ""}
                      {fullName(emp)}
                    </p>
                    <p className="truncate text-[11px] text-muted-foreground">
                      {emp.phone || emp.email || "—"}
                    </p>
                  </div>
                  <div className="flex shrink-0 flex-col items-end gap-1">
                    <EmployeeStatusBadge
                      value={emp.status}
                      onboarding={inOnboarding}
                    />
                    {incomplete && !inOnboarding && (
                      <span
                        data-testid={`incomplete-badge-card-${emp._id}`}
                        className="inline-flex items-center gap-0.5 rounded-full border border-rose-300/70 bg-rose-50 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider text-rose-700 dark:border-rose-500/30 dark:bg-rose-500/15 dark:text-rose-300"
                      >
                        <AlertTriangle className="h-2.5 w-2.5" />
                        Hiányos
                      </span>
                    )}
                  </div>
                </button>
              );
            })
          )}
        </div>

        <div className="hidden overflow-x-auto md:block">
          <table className="w-full text-sm" data-testid="employees-table">
            <thead>
              <tr className="border-b border-border bg-muted/30 text-muted-foreground">
                <Th>
                  <span className="inline-flex items-center gap-1.5">
                    <ArrowDownAZ className="h-3 w-3" strokeWidth={1.75} />
                    Név
                  </span>
                </Th>
                <Th className="hidden md:table-cell">Elérhetőség</Th>
                <Th className="hidden lg:table-cell">TAJ</Th>
                <Th className="hidden lg:table-cell">Adóazonosító</Th>
                <Th className="hidden md:table-cell">Születési dátum</Th>
                <Th>Állapot</Th>
                <Th className="pr-4 text-right">Műveletek</Th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <SkeletonRows />
              ) : filtered.length === 0 ? (
                <tr>
                  <td colSpan={7}>
                    <EmptyState
                      onAction={() => setOpen(true)}
                      hasQuery={query.length > 0 || statusFilter !== "all"}
                    />
                  </td>
                </tr>
              ) : (
                filtered.map((emp) => {
                  const incomplete = isIncomplete(emp);
                  const inOnboarding = emp.onboarding_complete === false;
                  return (
                  <tr
                    key={emp._id}
                    data-testid={`employee-row-${emp._id}`}
                    data-incomplete={incomplete ? "true" : "false"}
                    data-onboarding={inOnboarding ? "true" : "false"}
                    onClick={() => navigate(`/employees/${emp._id}`)}
                    className={cn(
                      "cursor-pointer border-b border-border last:border-0 transition-colors",
                      inOnboarding
                        ? "bg-indigo-50/40 hover:bg-indigo-50/70 dark:bg-indigo-500/[0.05] dark:hover:bg-indigo-500/[0.08]"
                        : "hover:bg-foreground/[0.025]",
                    )}
                  >
                    <Td className={cn(inOnboarding && "border-l-2 border-l-indigo-400")}>
                      <div className="flex items-center gap-3">
                        <span className="grid h-8 w-8 place-items-center rounded-full bg-foreground/90 text-background text-[10px] font-semibold">
                          {initials(emp)}
                        </span>
                        <div className="flex flex-col leading-tight">
                          <span className="font-medium">
                            {emp.titulus ? `${emp.titulus} ` : ""}
                            {fullName(emp)}
                          </span>
                          <span className="text-[11px] text-muted-foreground">
                            {emp.birth_place || "—"}
                            {emp.citizenship ? ` · ${emp.citizenship}` : ""}
                          </span>
                        </div>
                      </div>
                    </Td>
                    <Td className="hidden md:table-cell">
                      <div className="flex flex-col gap-0.5 text-[12.5px]">
                        {emp.email && (
                          <span className="inline-flex items-center gap-1.5 font-mono text-muted-foreground">
                            <Mail className="h-3 w-3" strokeWidth={1.75} />
                            {emp.email}
                          </span>
                        )}
                        {emp.phone && (
                          <span className="inline-flex items-center gap-1.5 text-muted-foreground">
                            <Phone className="h-3 w-3" strokeWidth={1.75} />
                            {emp.phone}
                          </span>
                        )}
                        {!emp.email && !emp.phone && (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </div>
                    </Td>
                    <Td className="hidden lg:table-cell">
                      <span className="font-mono text-[12px] text-muted-foreground">
                        {emp.taj || "—"}
                      </span>
                    </Td>
                    <Td className="hidden lg:table-cell">
                      <span className="font-mono text-[12px] text-muted-foreground">
                        {emp.tax_id || "—"}
                      </span>
                    </Td>
                    <Td className="hidden text-muted-foreground md:table-cell">{formatDateHu(emp.birth_date)}</Td>
                    <Td>
                      <div className="flex flex-col items-start gap-1">
                        <EmployeeStatusBadge
                          value={emp.status}
                          onboarding={emp.onboarding_complete === false}
                        />
                        {incomplete && !(emp.onboarding_complete === false) && (
                          <span
                            data-testid={`incomplete-badge-${emp._id}`}
                            className="inline-flex items-center gap-1 rounded-full border border-rose-300/70 bg-rose-50 px-1.5 py-0.5 text-[9.5px] font-semibold uppercase tracking-wider text-rose-700 dark:border-rose-500/30 dark:bg-rose-500/15 dark:text-rose-300"
                          >
                            <AlertTriangle className="h-2.5 w-2.5" />
                            Hiányos
                          </span>
                        )}
                      </div>
                    </Td>
                    <Td
                      className="pr-2 text-right"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <button
                            data-testid={`row-actions-${emp._id}`}
                            aria-label="Sor műveletek"
                            className="grid h-8 w-8 place-items-center rounded-md text-muted-foreground hover:bg-foreground/[0.06] hover:text-foreground"
                          >
                            <MoreHorizontal className="h-4 w-4" />
                          </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-44">
                          <DropdownMenuLabel>Műveletek</DropdownMenuLabel>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            data-testid={`view-employee-${emp._id}`}
                            onClick={() => navigate(`/employees/${emp._id}`)}
                          >
                            <Pencil className="h-4 w-4" />
                            Adatlap megnyitása
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            data-testid={`delete-employee-${emp._id}`}
                            onClick={() => onDelete(emp)}
                            className="text-destructive focus:text-destructive"
                          >
                            <Trash2 className="h-4 w-4" />
                            Törlés
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </Td>
                  </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      <EmployeeFormSheet
        open={open}
        onOpenChange={setOpen}
        employee={null}
        onSaved={(saved) => {
          if (saved) {
            setItems((prev) => {
              const idx = prev.findIndex((e) => e._id === saved._id);
              if (idx >= 0) {
                const next = [...prev];
                next[idx] = { ...next[idx], ...saved };
                return next;
              }
              return [saved, ...prev];
            });
          }
          fetchAll();
        }}
      />
    </div>
  );
}

function Th({ children, className = "" }) {
  return (
    <th
      className={cn(
        "h-10 whitespace-nowrap px-4 text-left align-middle text-[11px] font-medium uppercase tracking-[0.12em]",
        className
      )}
    >
      {children}
    </th>
  );
}

function Td({ children, className = "", ...rest }) {
  return (
    <td
      className={cn("h-16 whitespace-nowrap px-4 align-middle", className)}
      {...rest}
    >
      {children}
    </td>
  );
}

const EXTRA_DOT_TONES = {
  indigo: "bg-indigo-500",
  rose: "bg-rose-500",
};

function StatCard({ label, value, testId, tone, onClick, active }) {
  const dot =
    (tone && (STATUS_DOT_TONES[tone] || EXTRA_DOT_TONES[tone])) ||
    "bg-foreground/30";
  const clickable = typeof onClick === "function";
  return (
    <button
      type="button"
      data-testid={testId}
      onClick={clickable ? onClick : undefined}
      disabled={!clickable}
      className={cn(
        "rounded-xl border bg-[hsl(var(--sipx-elevated))] p-5 text-left transition-colors",
        active
          ? "border-indigo-400 ring-2 ring-indigo-200/60 dark:ring-indigo-500/30"
          : "border-border",
        clickable && "hover:bg-foreground/[0.025] cursor-pointer",
      )}
    >
      <p className="inline-flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-[0.14em] text-muted-foreground">
        <span className={cn("h-1.5 w-1.5 rounded-full", dot)} />
        {label}
      </p>
      <p className="mt-2 font-mono text-2xl font-semibold tracking-tight">{value}</p>
    </button>
  );
}

function SkeletonRows() {
  return (
    <>
      {[0, 1, 2, 3].map((i) => (
        <tr key={i} className="border-b border-border last:border-0">
          {[0, 1, 2, 3, 4, 5, 6].map((j) => (
            <td key={j} className="h-16 px-4">
              <div className="h-3 w-3/4 animate-pulse rounded bg-muted" />
            </td>
          ))}
        </tr>
      ))}
    </>
  );
}

function EmptyState({ onAction, hasQuery }) {
  return (
    <div
      data-testid="employees-empty-state"
      className="flex flex-col items-center justify-center gap-3 px-6 py-16 text-center"
    >
      <div className="grid h-10 w-10 place-items-center rounded-full border border-border bg-background">
        <IdCardIcon className="h-4 w-4 text-muted-foreground" strokeWidth={1.75} />
      </div>
      <div className="space-y-1">
        <p className="text-sm font-medium">
          {hasQuery ? "Nincs találat" : "Nincsenek munkavállalók"}
        </p>
        <p className="text-xs text-muted-foreground">
          {hasQuery
            ? "Próbálj meg másik keresést, vagy állítsd vissza a szűrőt."
          : "A munkavállalók csak a Toborzás onboarding-jén keresztül kerülnek be a rendszerbe."}
        </p>
      </div>
    </div>
  );
}
